import type {
  Agent,
  Analytics,
  CalendarEvent,
  CostResult,
  CreditCard,
  DashboardEvent,
  FinanceSummary,
  ModelPricing,
  PaymentRecord,
  ProjectionMonth,
  Session,
  Stats,
  Subscription,
} from "./types";

const BASE = "/api";

export function getAuthToken(): string | null {
  return localStorage.getItem("sorv_access_code");
}

export function setAuthToken(code: string) {
  localStorage.setItem("sorv_access_code", code);
}

export function clearAuthToken() {
  localStorage.removeItem("sorv_access_code");
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...((options?.headers as Record<string, string>) ?? {}),
  };
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export const api = {
  stats: {
    get: () => request<Stats>("/stats"),
  },

  sessions: {
    list: (params?: { status?: string; limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set("status", params.status);
      if (params?.limit) qs.set("limit", String(params.limit));
      if (params?.offset) qs.set("offset", String(params.offset));
      const q = qs.toString();
      return request<{ sessions: Session[] }>(`/sessions${q ? `?${q}` : ""}`);
    },
    get: (id: string) =>
      request<{ session: Session; agents: Agent[]; events: DashboardEvent[] }>(
        `/sessions/${encodeURIComponent(id)}`
      ),
  },

  agents: {
    list: (params?: { status?: string; session_id?: string; limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set("status", params.status);
      if (params?.session_id) qs.set("session_id", params.session_id);
      if (params?.limit) qs.set("limit", String(params.limit));
      if (params?.offset) qs.set("offset", String(params.offset));
      const q = qs.toString();
      return request<{ agents: Agent[] }>(`/agents${q ? `?${q}` : ""}`);
    },
  },

  events: {
    list: (params?: { session_id?: string; limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      if (params?.session_id) qs.set("session_id", params.session_id);
      if (params?.limit) qs.set("limit", String(params.limit));
      if (params?.offset) qs.set("offset", String(params.offset));
      const q = qs.toString();
      return request<{ events: DashboardEvent[] }>(`/events${q ? `?${q}` : ""}`);
    },
  },

  analytics: {
    get: () => request<Analytics>("/analytics"),
  },

  settings: {
    info: () =>
      request<{
        db: { path: string; size: number; counts: Record<string, number> };
        hooks: { installed: boolean; path: string; hooks: Record<string, boolean> };
        server: { uptime: number; node_version: string; platform: string; ws_connections: number };
      }>("/settings/info"),
    clearData: () =>
      request<{ ok: boolean; cleared: Record<string, number> }>("/settings/clear-data", {
        method: "POST",
      }),
    reimport: () =>
      request<{ ok: boolean; imported: number; skipped: number; errors: number }>(
        "/settings/reimport",
        { method: "POST" }
      ),
    reinstallHooks: () =>
      request<{ ok: boolean; hooks: { installed: boolean; hooks: Record<string, boolean> } }>(
        "/settings/reinstall-hooks",
        { method: "POST" }
      ),
    resetPricing: () =>
      request<{ ok: boolean; pricing: ModelPricing[] }>("/settings/reset-pricing", {
        method: "POST",
      }),
    exportData: () => `${BASE}/settings/export`,
    cleanup: (params: { abandon_hours?: number; purge_days?: number }) =>
      request<{
        ok: boolean;
        abandoned: number;
        purged_sessions: number;
        purged_events: number;
        purged_agents: number;
      }>("/settings/cleanup", { method: "POST", body: JSON.stringify(params) }),
  },

  pricing: {
    list: () => request<{ pricing: ModelPricing[] }>("/pricing"),
    upsert: (data: Omit<ModelPricing, "updated_at">) =>
      request<{ pricing: ModelPricing }>("/pricing", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (pattern: string) =>
      request<{ ok: boolean }>(`/pricing/${encodeURIComponent(pattern)}`, {
        method: "DELETE",
      }),
    totalCost: () => request<CostResult>("/pricing/cost"),
    sessionCost: (sessionId: string) =>
      request<CostResult>(`/pricing/cost/${encodeURIComponent(sessionId)}`),
  },

  finances: {
    summary: () => request<FinanceSummary>("/finances/summary"),
    cards: () => request<{ cards: CreditCard[] }>("/finances/cards"),
    updateCard: (id: string, data: Partial<CreditCard>) =>
      request<{ card: CreditCard }>(`/finances/cards/${encodeURIComponent(id)}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    pay: (id: string, amount: number, paymentType?: string, note?: string) =>
      request<{ card: CreditCard; payment: PaymentRecord }>(
        `/finances/cards/${encodeURIComponent(id)}/pay`,
        { method: "POST", body: JSON.stringify({ amount, payment_type: paymentType, note }) }
      ),
    payments: (limit = 20) =>
      request<{ payments: PaymentRecord[] }>(`/finances/payments?limit=${limit}`),
    subscriptions: () =>
      request<{ subscriptions: Subscription[] }>("/finances/subscriptions"),
    updateSubscription: (id: number, data: Partial<Subscription>) =>
      request<{ subscription: Subscription }>(`/finances/subscriptions/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    calendar: () => request<{ events: CalendarEvent[] }>("/finances/calendar"),
    projection: (months = 18, monthlyPayment = 8000) =>
      request<{ snapshots: ProjectionMonth[] }>(
        `/finances/projection?months=${months}&monthlyPayment=${monthlyPayment}`
      ),
  },

  sorv: {
    projects: () => request<{ projects: unknown[] }>("/sorv/projects"),
    tasks: {
      list: (params?: { projectId?: string; status?: string }) => {
        const qs = new URLSearchParams();
        if (params?.projectId) qs.set("projectId", params.projectId);
        if (params?.status) qs.set("status", params.status);
        const q = qs.toString();
        return request<{ tasks: unknown[] }>(`/sorv/tasks${q ? `?${q}` : ""}`);
      },
      create: (data: { projectId: string; agentId?: string | null; instruction: string }) =>
        request<{ task: unknown }>("/sorv/tasks", {
          method: "POST",
          body: JSON.stringify(data),
        }),
    },
    sync: (projectId: string) =>
      request<{ sync: unknown }>(`/sorv/sync/${encodeURIComponent(projectId)}`, {
        method: "POST",
      }),
  },
};
